import { useState, useEffect, useCallback, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { logger } from '@/lib/logger';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Handshake, 
  Search, 
  TrendingUp,
  DollarSign,
  FileText,
  Video,
  CheckCircle,
  XCircle,
  Loader2,
  Plus,
  ExternalLink,
  Building2,
  User,
  Briefcase,
  Target,
  Percent,
  Filter,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { CRMPageHeader } from '@/components/crm/CRMPageHeader';
import { FlowInlineLauncher } from '@/components/flow/FlowWidget';

interface Deal {
  id: string;
  company: string;
  contact_name: string;
  contact_email: string;
  stage: 'new' | 'qualified' | 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost';
  value: number | null;
  source: string;
  created_at: string;
  updated_at: string;
  tags: string[];
  // Deal-specific fields
  equity_percent?: number;
  valuation?: number;
  round_type?: string;
  lead_investor?: string;
  next_meeting?: string;
  term_sheet_sent?: boolean;
  due_diligence_started?: boolean;
}

const dealStages = [
  {
    id: 'new',
    label: 'New Match',
    color: 'bg-blue-500',
    gradient: 'from-blue-400/80 via-blue-500/70 to-indigo-500/80',
    softColor: 'bg-blue-500/15',
    textColor: 'text-blue-700 dark:text-blue-300',
    description: 'Initial interest expressed',
  },
  {
    id: 'qualified',
    label: 'First Meeting',
    color: 'bg-purple-500',
    gradient: 'from-violet-400/80 via-purple-500/70 to-fuchsia-500/80',
    softColor: 'bg-purple-500/15',
    textColor: 'text-purple-700 dark:text-purple-300',
    description: 'Intro call scheduled/completed',
  },
  {
    id: 'proposal',
    label: 'Term Sheet',
    color: 'bg-amber-500',
    gradient: 'from-amber-400/80 via-orange-400/70 to-amber-500/80',
    softColor: 'bg-amber-500/15',
    textColor: 'text-amber-700 dark:text-amber-300',
    description: 'Terms proposed or negotiated',
  },
  {
    id: 'negotiation',
    label: 'Due Diligence',
    color: 'bg-orange-500',
    gradient: 'from-orange-400/80 via-red-400/60 to-orange-500/80',
    softColor: 'bg-orange-500/15',
    textColor: 'text-orange-700 dark:text-orange-300',
    description: 'Deep dive and final negotiations',
  },
  {
    id: 'closed_won',
    label: 'Closed ✓',
    color: 'bg-green-500',
    gradient: 'from-emerald-400/80 via-green-500/70 to-teal-500/80',
    softColor: 'bg-green-500/15',
    textColor: 'text-green-700 dark:text-green-300',
    description: 'Investment completed',
  },
  {
    id: 'closed_lost',
    label: 'Passed ✗',
    color: 'bg-red-500',
    gradient: 'from-rose-400/80 via-red-500/70 to-pink-500/80',
    softColor: 'bg-red-500/15',
    textColor: 'text-red-700 dark:text-red-300',
    description: 'Deal declined or withdrawn',
  },
];

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export function DealsPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [expandedStage, setExpandedStage] = useState<string | null>('new');
  const { toast } = useToast();
  const [showAdd, setShowAdd] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newDeal, setNewDeal] = useState({ company: '', stage: 'new', value: '', contactId: 'none' });
  const [contactOptions, setContactOptions] = useState<{ id: string; name: string }[]>([]);

  const fetchDeals = useCallback(async () => {
    if (!token) return;
    
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/crm/deals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please sign in again.');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to load deals: ${response.statusText}`);
      }
      
      const data = await response.json();
      // Use real deal data from API - no mock data generation
      const deals = (data.deals || []).map((d: any) => ({
        ...d,
        equity_percent: d.equity_percent || null,
        valuation: d.valuation || null,
        round_type: d.round_type || null,
        lead_investor: d.lead_investor || null,
        next_meeting: d.next_meeting || null,
        term_sheet_sent: d.term_sheet_sent || false,
        due_diligence_started: d.due_diligence_started || false,
      }));
      
      setDeals(deals);
      logger.debug('Deals loaded successfully', { count: deals.length });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load deals';
      setError(message);
      logger.error('Failed to fetch deals', err, {
        component: 'DealsPage',
        timestamp: new Date().toISOString(),
      });
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  // Populate the contact select when the New Deal modal opens. The contacts
  // API speaks camelCase ({id, name}); the deals POST body is snake_case.
  useEffect(() => {
    if (!showAdd || !token) return;
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/crm/contacts?limit=100`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return;
        const data = await response.json();
        setContactOptions(
          (data.contacts || []).map((c: any) => ({ id: c.id, name: c.name }))
        );
      } catch {
        // Select still renders with just "No contact"
      }
    })();
  }, [showAdd, token]);

  const createDeal = async () => {
    if (!token) return;
    const company = newDeal.company.trim();
    const value = newDeal.value === '' ? undefined : Number(newDeal.value);
    if (!company) {
      toast({ variant: 'destructive', title: 'Company is required' });
      return;
    }
    if (value !== undefined && (!Number.isFinite(value) || value < 0)) {
      toast({ variant: 'destructive', title: 'Value must be a non-negative number' });
      return;
    }

    setIsCreating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/crm/deals`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company,
          stage: newDeal.stage,
          value,
          contact_id: newDeal.contactId === 'none' ? undefined : newDeal.contactId,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to create deal');
      }
      toast({ title: 'Deal created' });
      setShowAdd(false);
      setNewDeal({ company: '', stage: 'new', value: '', contactId: 'none' });
      await fetchDeals();
    } catch (err) {
      logger.error('Failed to create deal', err, { component: 'DealsPage' });
      toast({
        variant: 'destructive',
        title: err instanceof Error ? err.message : 'Failed to create deal',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const updateDealStage = async (dealId: string, newStage: Deal['stage']) => {
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/crm/deals/${dealId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ stage: newStage }),
      });

      if (!response.ok) {
        throw new Error('Failed to update deal stage');
      }

      setDeals(prev => prev.map(d =>
        d.id === dealId ? { ...d, stage: newStage } : d
      ));
      // Keep the open detail modal's stage rows in sync (refetch replaces
      // the deals list but selectedDeal is separate state).
      setSelectedDeal(prev =>
        prev && prev.id === dealId ? { ...prev, stage: newStage } : prev
      );
      await fetchDeals();
      logger.debug('Deal stage updated', { dealId, newStage });
    } catch (error) {
      logger.error('Failed to update deal stage', error, { dealId, newStage });
      toast({ variant: 'destructive', title: 'Failed to update deal stage' });
      await fetchDeals();
    }
  };

  const filteredDeals = deals.filter(deal => 
    searchQuery === '' || 
    deal.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    deal.contact_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStageProgress = (stage: string) => {
    const stageIndex = dealStages.findIndex(s => s.id === stage);
    return ((stageIndex + 1) / dealStages.length) * 100;
  };

  const totalPipelineValue = deals.reduce((sum, d) => sum + (d.value || 0), 0);
  const activeDeals = deals.filter(d => !['closed_won', 'closed_lost'].includes(d.stage)).length;
  const closedWonValue = deals.filter(d => d.stage === 'closed_won').reduce((sum, d) => sum + (d.value || 0), 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <div className="h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
          <XCircle className="h-8 w-8 text-red-500" />
        </div>
        <h3 className="text-xl font-semibold">Unable to load deals</h3>
        <p className="text-sm text-muted-foreground max-w-md text-center">
          We're having trouble retrieving your deal data. Please try again or contact support if the problem persists.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => fetchDeals()}>
            Retry
          </Button>
          <Button onClick={() => navigate('/crm/dashboard')}>
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <CRMPageHeader
        title="Deal Flow"
        description="Track investors through stages, meetings, and closed rounds"
        actions={(
          <>
            <FlowInlineLauncher className="shrink-0" />
            <div className="relative min-w-[200px] flex-1 max-w-md sm:min-w-[280px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search deals by company or investor..."
                value={searchQuery}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" disabled title="Coming soon">
              <Filter className="w-4 h-4 mr-2" /> Filter
            </Button>
            <Button onClick={() => setShowAdd(true)}>
              <Plus className="w-4 h-4 mr-2" /> New Deal
            </Button>
          </>
        )}
      />

      {/* Header Stats */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card className="rounded-2xl border bg-card p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Active Deals</p>
              <p className="text-2xl font-bold">{activeDeals}</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/10">
              <Handshake className="w-5 h-5 text-blue-500" />
            </div>
          </div>
        </Card>
        
        <Card className="rounded-2xl border bg-card p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Pipeline Value</p>
              <p className="text-2xl font-bold">${(totalPipelineValue / 1000000).toFixed(1)}M</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/10">
              <DollarSign className="w-5 h-5 text-amber-500" />
            </div>
          </div>
        </Card>
        
        <Card className="rounded-2xl border bg-card p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Closed Won</p>
              <p className="text-2xl font-bold">${(closedWonValue / 1000000).toFixed(1)}M</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-500/10">
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
          </div>
        </Card>
        
        <Card className="rounded-2xl border bg-card p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Avg Deal Size</p>
              <p className="text-2xl font-bold">
                ${deals.length > 0 ? Math.round(totalPipelineValue / deals.length / 1000) : 0}K
              </p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-500/10">
              <TrendingUp className="w-5 h-5 text-purple-500" />
            </div>
          </div>
        </Card>
      </div>

      {/* Deal Pipeline */}
      <div className="space-y-3">
        {dealStages.map((stage) => {
          const stageDeals = filteredDeals.filter(d => d.stage === stage.id);
          const stageValue = stageDeals.reduce((sum, d) => sum + (d.value || 0), 0);
          const isExpanded = expandedStage === stage.id;
          
          return (
            <div key={stage.id} className="overflow-hidden rounded-2xl border bg-card shadow-sm">
              {/* Stage Header */}
              <div 
                className={cn(
                  "flex cursor-pointer items-center justify-between px-4 py-3 transition-shadow duration-200 hover:bg-muted/30"
                )}
                onClick={() => setExpandedStage(isExpanded ? null : stage.id)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={cn('h-2.5 w-2.5 flex-shrink-0 rounded-full', stage.color)} />
                  <div className="min-w-0">
                    <h3 className="truncate font-semibold text-foreground">{stage.label}</h3>
                    <p className="truncate text-xs text-muted-foreground">{stage.description}</p>
                  </div>
                  <Badge variant="secondary" className="ml-2 shrink-0">
                    {stageDeals.length}
                  </Badge>
                </div>
                <div className="flex items-center gap-4">
                  {stageValue > 0 && (
                    <span className="text-sm font-medium text-muted-foreground">
                      ${(stageValue / 1000).toFixed(0)}K
                    </span>
                  )}
                  {isExpanded ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                </div>
              </div>
              
              {/* Stage Deals */}
              {isExpanded && (
                <div className="border-t bg-muted/20 p-4">
                  {stageDeals.length === 0 ? (
                    <p className="text-center text-muted-foreground py-6 text-sm">No deals in this stage</p>
                  ): (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {stageDeals.map((deal) => (
                        <motion.div
                          key={deal.id}
                          layout
                          className="group cursor-pointer rounded-2xl border bg-card p-4 shadow-sm transition-shadow hover:shadow-md"
                          onClick={() => { setSelectedDeal(deal); setIsDetailModalOpen(true); }}
                        >
                          {/* Deal Header */}
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="font-semibold transition-colors group-hover:text-primary">
                                {deal.company}
                              </h4>
                              <p className="text-xs text-muted-foreground">{deal.contact_name}</p>
                            </div>
                            <div className="flex items-center gap-1">
                              {deal.tags?.includes('match') && (
                                <Badge variant="outline" className="text-xs">
                                  <Target className="w-3 h-3 mr-1" />
                                  Match
                                </Badge>
                              )}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-xs"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    Move
                                    <ChevronDown className="w-3 h-3 ml-1" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {dealStages
                                    .filter((s) => s.id !== deal.stage)
                                    .map((s) => (
                                      <DropdownMenuItem
                                        key={s.id}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          updateDealStage(deal.id, s.id as Deal['stage']);
                                        }}
                                      >
                                        <span className={cn('mr-2 h-2 w-2 rounded-full', s.color)} />
                                        {s.label}
                                      </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                          
                          {/* Deal Metrics */}
                          <div className="space-y-2 mb-3">
                            {deal.value && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">Investment</span>
                                <span className="font-medium text-green-600">
                                  ${(deal.value / 1000).toFixed(0)}K
                                </span>
                              </div>
                            )}
                            {deal.equity_percent && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">Equity</span>
                                <span className="font-medium">{deal.equity_percent}%</span>
                              </div>
                            )}
                            {deal.valuation && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">Valuation</span>
                                <span className="font-medium">${(deal.valuation / 1000000).toFixed(1)}M</span>
                              </div>
                            )}
                            {deal.round_type && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">Round</span>
                                <Badge variant="secondary" className="text-xs">
                                  {deal.round_type}
                                </Badge>
                              </div>
                            )}
                          </div>
                          
                          {/* Deal Progress */}
                          <div className="pt-3 border-t">
                            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                              <span>Deal Progress</span>
                              <span>{Math.round(getStageProgress(deal.stage))}%</span>
                            </div>
                            <Progress value={getStageProgress(deal.stage)} className="h-1" />
                          </div>
                          
                          {/* Quick Actions */}
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                            {deal.term_sheet_sent && (
                              <Badge variant="outline" className="text-xs">
                                <FileText className="w-3 h-3 mr-1" />
                                Term Sheet
                              </Badge>
                            )}
                            {deal.due_diligence_started && (
                              <Badge variant="outline" className="text-xs">
                                <Briefcase className="w-3 h-3 mr-1" />
                                DD Active
                              </Badge>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Deal Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Handshake className="w-5 h-5" />
              Deal Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedDeal && (
            <div className="space-y-6">
              {/* Deal Parties */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-4 bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Company</p>
                      <p className="font-semibold">{selectedDeal.company}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Investor</p>
                      <p className="font-semibold">{selectedDeal.contact_name}</p>
                    </div>
                  </div>
                </Card>
              </div>
              
              {/* Deal Terms */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <DollarSign className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">Investment</p>
                  <p className="text-xl font-bold">
                    {selectedDeal.value ? `$${(selectedDeal.value / 1000).toFixed(0)}K` : 'TBD'}
                  </p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <Percent className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">Equity</p>
                  <p className="text-xl font-bold">
                    {selectedDeal.equity_percent ? `${selectedDeal.equity_percent}%` : 'TBD'}
                  </p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <Target className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">Valuation</p>
                  <p className="text-xl font-bold">
                    {selectedDeal.valuation ? `$${(selectedDeal.valuation / 1000000).toFixed(1)}M` : 'TBD'}
                  </p>
                </div>
              </div>
              
              {/* Deal Progress */}
              <div>
                <h4 className="font-medium mb-3">Deal Progress</h4>
                <div className="space-y-2">
                  {dealStages.map((stage, idx) => {
                    const isCompleted = dealStages.findIndex(s => s.id === selectedDeal.stage) >= idx;
                    const isCurrent = stage.id === selectedDeal.stage;
                    
                    return (
                      <div
                        key={stage.id}
                        role="button"
                        onClick={() => {
                          if (!isCurrent) {
                            updateDealStage(selectedDeal.id, stage.id as Deal['stage']);
                          }
                        }}
                        className={cn(
                          "flex items-center gap-3 p-2 rounded-lg",
                          isCurrent && "bg-primary/10",
                          isCompleted && !isCurrent && "opacity-50",
                          !isCurrent && "cursor-pointer hover:bg-muted/50"
                        )}
                      >
                        <div className={cn(
                          "w-6 h-6 rounded-full flex items-center justify-center text-xs",
                          isCompleted ? stage.color : "bg-muted",
                          "text-white"
                        )}>
                          {isCompleted ? '✓' : idx + 1}
                        </div>
                        <span className={cn("text-sm", isCurrent && "font-medium")}>
                          {stage.label}
                        </span>
                        {isCurrent && (
                          <Badge variant="outline" className="ml-auto text-xs">
                            Current
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                {selectedDeal.term_sheet_sent ? (
                  <Button className="flex-1" disabled title="Coming soon">
                    <FileText className="w-4 h-4 mr-2" /> View Term Sheet
                  </Button>
                ) : (
                  <Button className="flex-1" disabled variant="outline" title="No term sheet available">
                    <FileText className="w-4 h-4 mr-2" /> View Term Sheet
                  </Button>
                )}
                <Button variant="outline" disabled title="Coming soon">
                  <Video className="w-4 h-4 mr-2" /> Schedule Meeting
                </Button>
                {selectedDeal.tags?.includes('match') && (
                  <Button variant="outline" onClick={() => window.open('/crm/discover', '_blank')}>
                    <ExternalLink className="w-4 h-4 mr-2" /> View in Matching
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Deal Modal */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Deal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Company *</Label>
              <Input
                value={newDeal.company}
                onChange={(e) => setNewDeal({ ...newDeal, company: e.target.value })}
                placeholder="Acme Ventures"
              />
            </div>
            <div>
              <Label>Stage</Label>
              <Select
                value={newDeal.stage}
                onValueChange={(v) => setNewDeal({ ...newDeal, stage: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {dealStages.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Investment Value ($)</Label>
              <Input
                type="number"
                min="0"
                value={newDeal.value}
                onChange={(e) => setNewDeal({ ...newDeal, value: e.target.value })}
                placeholder="250000"
              />
            </div>
            <div>
              <Label>Contact</Label>
              <Select
                value={newDeal.contactId}
                onValueChange={(v) => setNewDeal({ ...newDeal, contactId: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No contact</SelectItem>
                  {contactOptions.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowAdd(false)}>
                Cancel
              </Button>
              <Button
                className="flex-1"
                onClick={createDeal}
                disabled={!newDeal.company.trim() || isCreating}
              >
                {isCreating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
